import axios from 'axios';
import { Capacitor } from '@capacitor/core';
import { AUTH_API_URL } from '../config';
import { getRefreshToken, setRefreshToken, clearRefreshToken } from './sessionVault';

let generation = 0;
let pending = null;
let writes = Promise.resolve();
const native = () => Capacitor.getPlatform() === 'android';
const origin = AUTH_API_URL.replace(/\/$/, '');
const enqueue = (work) => { writes = writes.catch(() => {}).then(work); return writes; };
export function tokenClaims(token) {
  try {
    const part = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(part.padEnd(Math.ceil(part.length / 4) * 4, '=')));
  } catch { return {}; }
}
const identity = (token) => {
  const value = tokenClaims(token);
  return String(value.userId || value.user_id || value.sub || '');
};
const unauthorized = () => Object.assign(new Error('Mobile session rejected'), { response: { status: 401 } });

// Only the long-lived credential enters the encrypted vault. Access tokens
// remain short lived and compatible with the existing request clients.
export async function rememberMobileLogin(data) {
  if (!native()) return;
  const current = ++generation;
  pending = null;
  await enqueue(async () => {
    if (current !== generation) return;
    if (data.refreshToken && identity(data.token)) {
      await setRefreshToken(JSON.stringify({ refreshToken: data.refreshToken, userId: identity(data.token), origin }));
    } else await clearRefreshToken();
  });
}

export async function renewMobileSession() {
  const access = localStorage.getItem('token');
  if (!native() || navigator.onLine === false) return access;
  const claims = tokenClaims(access || '');
  if (claims.exp && claims.exp * 1000 > Date.now() + 5 * 60 * 1000) return access;
  if (pending) return pending;
  const current = generation;
  const work = (async () => {
    await writes.catch(() => {});
    let session;
    try { session = JSON.parse(await getRefreshToken() || 'null'); } catch { session = null; }
    if (!session?.refreshToken) return access;
    const userId = localStorage.getItem('userId');
    if (session.origin !== origin || !userId || session.userId !== userId) {
      await enqueue(async () => { if (current === generation) await clearRefreshToken(); });
      throw unauthorized();
    }
    try {
      // Persist an idempotency key before sending: a lost response or process
      // restart must retry the same rotation, not invalidate the session.
      if (!session.requestId) {
        session.requestId = typeof window.crypto.randomUUID === 'function' ? window.crypto.randomUUID()
          : Array.from(window.crypto.getRandomValues(new Uint8Array(16)), (byte) => byte.toString(16).padStart(2, '0')).join('');
        await enqueue(async () => { if (current === generation) await setRefreshToken(JSON.stringify(session)); });
      }
      if (current !== generation) return null;
      const { data } = await axios.post(`${origin}/mobile-session/refresh`, { refreshToken: session.refreshToken, requestId: session.requestId }, { timeout: 10000 });
      if (current !== generation) return null;
      if (!data.token || !data.refreshToken || identity(data.token) !== userId) throw unauthorized();
      await enqueue(async () => {
        if (current === generation) await setRefreshToken(JSON.stringify({ refreshToken: data.refreshToken, userId: session.userId, origin }));
      });
      if (current !== generation) return null;
      localStorage.setItem('token', data.token);
      return data.token;
    } catch (error) {
      if ([401, 403].includes(error.response?.status)) {
        await enqueue(async () => { if (current === generation) await clearRefreshToken(); });
        if (current === generation) localStorage.removeItem('token');
      }
      throw error;
    }
  })();
  pending = work;
  try { return await work; } finally { if (pending === work) pending = null; }
}

export async function forgetMobileSession() {
  if (!native()) return;
  generation += 1;
  pending = null;
  let session;
  await enqueue(async () => {
    try { session = JSON.parse(await getRefreshToken() || 'null'); } catch { session = null; }
    await clearRefreshToken();
  });
  if (session?.refreshToken && session.origin === origin) {
    try { await axios.post(`${origin}/mobile-session/logout`, { refreshToken: session.refreshToken }, { timeout: 5000 }); } catch { /* Local deletion works offline; server expiry still applies. */ }
  }
}
